import { useEffect, useState, useMemo } from 'react'
import { cartService, CartSession, CartStatistics } from '@/services/cartService'
import useAppConfigStore from '@/stores/appConfigStore'
import { DetailHeader } from '@/components/ui/DetailHeader'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { 
  RefreshCw, 
  ShoppingCart, 
  Package, 
  DollarSign, 
  Clock,
  User,
  Store,
  Eye,
  XCircle,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const CartsPage = () => {
  const { systemConfig } = useAppConfigStore()
  const [sessions, setSessions] = useState<CartSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState<any[]>([])
  const [statistics, setStatistics] = useState<CartStatistics | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })
  const navigate = useNavigate()

  const loadCarts = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Load cart sessions
      const response = await cartService.getAll()
      if (response.success) {
        setSessions(response.data)
        setPagination(prev => ({
          ...prev,
          total: response.data.length
        }))
      } else {
        setError('Échec du chargement des paniers')
      }
      
      // Load statistics
      const statsResponse = await cartService.getStatistics()
      if (statsResponse.success && statsResponse.data) {
        setStatistics(statsResponse.data)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCarts()
    
    // Set up polling every 10 seconds for real-time updates
    const intervalId = setInterval(() => {
      loadCarts()
    }, 10000) // 10 seconds
    
    return () => {
      clearInterval(intervalId)
    }
  }, [])

  const formatCurrency = (amount: number) => {
    const currencyCode = systemConfig?.default_currency || 'MAD'
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatTimeRemaining = (expiresAt: string) => {
    const now = new Date()
    const expires = new Date(expiresAt)
    const diffMs = expires.getTime() - now.getTime()
    
    if (diffMs <= 0) return 'Expiré'
    
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes % 60}m`
    }
    return `${diffMinutes}m`
  }

  const getStatusBadgeVariant = (status: CartSession['status']) => {
    switch (status) {
      case 'active': return 'default'
      case 'expired': return 'destructive'
      case 'converted': return 'outline'
      default: return 'default'
    }
  }

  const getStatusLabel = (status: CartSession['status']) => {
    switch (status) {
      case 'active': return 'Actif'
      case 'expired': return 'Expiré'
      case 'converted': return 'Converti'
      default: return status
    }
  }

  const getStatusIcon = (status: CartSession['status']) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-3 w-3 mr-1" />
      case 'expired': return <XCircle className="h-3 w-3 mr-1" />
      case 'converted': return <AlertCircle className="h-3 w-3 mr-1" />
      default: return null
    }
  }

  const columns = useMemo(() => [
    {
      key: 'sessionId',
      label: 'Session ID',
      sortable: true,
      render: (value: string) => (
        <div className="font-mono text-sm">
          {value.substring(0, 8)}...
          <div className="text-xs text-gray-500 truncate max-w-xs">
            {value}
          </div>
        </div>
      )
    },
    {
      key: 'user',
      label: 'Utilisateur',
      sortable: true,
      render: (_value: any, row: CartSession) => (
        <div className="flex items-center">
          <User className="h-4 w-4 mr-2 text-gray-400" />
          <div>
            <div className="font-medium">
              {row.userEmail || 'Invité'}
            </div>
            {row.userId && (
              <div className="text-xs text-gray-500">
                {row.userId.substring(0, 8)}...
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'merchant',
      label: 'Commerçant',
      sortable: true,
      render: (_value: any, row: CartSession) => (
        <div className="flex items-center">
          <Store className="h-4 w-4 mr-2 text-gray-400" />
          <div className="text-sm">{row.merchantName || 'N/D'}</div>
        </div>
      )
    },
    {
      key: 'totalItems',
      label: 'Articles',
      sortable: true,
      render: (value: number) => (
        <div className="flex items-center">
          <Package className="h-4 w-4 mr-2 text-blue-500" />
          <div className="font-medium">{value}</div>
        </div>
      )
    },
    {
      key: 'totalValue',
      label: 'Valeur totale',
      sortable: true,
      render: (value: number) => (
        <div className="flex items-center">
          <DollarSign className="h-4 w-4 mr-2 text-green-500" />
          <div className="font-medium">{formatCurrency(value)}</div>
        </div>
      )
    },
    {
      key: 'lastActivity',
      label: 'Dernière activité',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm">{formatDate(value)}</div>
      )
    },
    {
      key: 'expiresAt',
      label: 'Temps restant',
      sortable: true,
      render: (value: string, row: CartSession) => {
        const timeRemaining = formatTimeRemaining(value)
        const isExpired = row.status === 'expired'
        
        return (
          <div className="flex items-center">
            <Clock className={`h-4 w-4 mr-2 ${isExpired ? 'text-gray-400' : 'text-orange-500'}`} />
            <div className={`font-medium ${isExpired ? 'text-gray-500' : ''}`}>
              {timeRemaining}
            </div>
          </div>
        )
      }
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (value: CartSession['status']) => (
        <Badge variant={getStatusBadgeVariant(value)} className="inline-flex items-center">
          {getStatusIcon(value)}
          {getStatusLabel(value)}
        </Badge>
      )
    }
  ], [systemConfig])

  const actions = useMemo(() => [
    {
      label: 'Voir détails',
      icon: <Eye className="h-4 w-4" />,
      onClick: (row: CartSession) => navigate(`/carts/${row.sessionId}`),
      variant: 'outline' as const
    },
    {
      label: 'Forcer expiration',
      icon: <XCircle className="h-4 w-4" />,
      onClick: async (row: CartSession) => {
        if (row.status === 'active') {
          if (confirm('Êtes-vous sûr de vouloir forcer l\'expiration de ce panier ?')) {
            try {
              const response = await cartService.forceExpire(row.sessionId)
              if (response.success) {
                alert('Panier expiré avec succès')
                loadCarts()
              } else {
                alert(`Erreur: ${response.error}`)
              }
            } catch (err) {
              alert('Erreur lors de l\'expiration du panier')
            }
          }
        }
      },
      variant: 'destructive' as const,
      disabled: (row: CartSession) => row.status !== 'active'
    }
  ], [navigate])

  const filterOptions = useMemo(() => [
    {
      key: 'status',
      label: 'Statut',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'active', label: 'Actif' },
        { value: 'expired', label: 'Expiré' },
        { value: 'converted', label: 'Converti' }
      ]
    },
    {
      key: 'minValue',
      label: 'Valeur minimale',
      type: 'number' as const,
      placeholder: '0'
    },
    {
      key: 'maxValue',
      label: 'Valeur maximale',
      type: 'number' as const,
      placeholder: '10000'
    }
  ], [])

  const handleFilterChange = (newFilters: any[]) => {
    setActiveFilters(newFilters)
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
  }

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = (newSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newSize, page: 1 }))
  }

  const filteredData = useMemo(() => {
    let result = sessions

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(session => 
        session.sessionId.toLowerCase().includes(query) ||
        (session.userEmail?.toLowerCase() || '').includes(query) ||
        (session.merchantName?.toLowerCase() || '').includes(query)
      )
    }

    // Apply other filters
    activeFilters.forEach(filter => {
      if (filter.key === 'status' && filter.value !== 'all') {
        result = result.filter(session => session.status === filter.value)
      }
      
      if (filter.key === 'minValue' && filter.value) {
        const minValue = parseFloat(filter.value)
        if (!isNaN(minValue)) {
          result = result.filter(session => session.totalValue >= minValue)
        }
      }
      
      if (filter.key === 'maxValue' && filter.value) {
        const maxValue = parseFloat(filter.value)
        if (!isNaN(maxValue)) {
          result = result.filter(session => session.totalValue <= maxValue)
        }
      }
    })

    // Update pagination total
    setPagination(prev => ({ ...prev, total: result.length }))

    return result
  }, [sessions, searchQuery, activeFilters])

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize
    const endIndex = startIndex + pagination.pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, pagination.page, pagination.pageSize])

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Paniers d\'achat', href: '/carts' }
  ]

  const headerActions = [
    {
      label: 'Actualiser',
      icon: <RefreshCw className="h-4 w-4 mr-2" />,
      onClick: loadCarts,
      variant: 'outline' as const,
      disabled: loading
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Paniers d'achat"
        subtitle="Surveillez les paniers actifs des utilisateurs avec produits et offres en temps réel"
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
          <div className="mt-4">
            <Button variant="outline" onClick={loadCarts}>
              Réessayer
            </Button>
          </div>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <ShoppingCart className="h-4 w-4 mr-2 text-blue-500" />
              Paniers actifs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics ? statistics.activeSessions : '...'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Sessions en cours
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Package className="h-4 w-4 mr-2 text-green-500" />
              Articles totaux
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics ? statistics.totalItems : '...'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Dans tous les paniers
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <DollarSign className="h-4 w-4 mr-2 text-orange-500" />
              Valeur totale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics ? formatCurrency(statistics.totalValue) : '...'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Valeur des paniers actifs
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Clock className="h-4 w-4 mr-2 text-red-500" />
              Expirent bientôt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics ? statistics.expiringSoon : '...'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Dans moins de 5 minutes
            </div>
          </CardContent>
        </Card>
      </div>

      <FilterBar
        searchPlaceholder="Rechercher par session ID, email ou commerçant..."
        filters={filterOptions}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={paginatedData}
        actions={actions}
        loading={loading}
        emptyMessage="Aucun panier d'achat trouvé"
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
      />

      {/* Real-time indicator */}
      <div className="flex items-center justify-end text-sm text-gray-500">
        <div className="flex items-center">
          <div className="h-2 w-2 rounded-full bg-green-500 mr-2 animate-pulse"></div>
          <span>Mise à jour en temps réel (10s)</span>
        </div>
      </div>
    </div>
  )
}

export default CartsPage